import Button from "@/common/elements/Button";
import CommonDatePicker from "@/common/elements/DatePicker";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import Table from "@/common/elements/Table";
import DynamicForm from "@/common/DynamicForm";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
  StyledSubTableContainer,
} from "@/common/styles";
import { PlusOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import CustomModal from "../CustomModal";
import Personal, { PERSON } from "../InmateDetails/Personal";
import { Form, message, notification } from "antd";
import {
  findAgefromDOB,
  getOptionsFromRefList,
  getRefListNameByID,
  hasCheckSecurity,
  isNonEmptyVariable,
  convertApiFields,
  getSQLTypeToHTMLType,
} from "@/util/common_function";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import useDialog from "@/hooks/useDialog";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SCHEMA_NAME,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import { Popconfirm } from "antd";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";
import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";

const BAIL_PAYMENT_BAILS_HEADERS = [
  { key: "bailAmount", text: "Bail Amount" },
  { key: "bailType", text: "Bail Type" },
  { key: "charge", text: "Charge" },
];

// Translates a GET-by-id inmate bio (fName/lName/mName shape) into the
// bail payment save payload's bio node (getfName/getlName/getmName shape).
// Everything else (ssn, race, height, weight, dob, bioAddresses, etc.) is
// the same field name in both shapes, so it passes through untouched -
// only the name fields are renamed and bioId is replaced with `bioId`.
const mapInmateBioToPayerBio = (inmateBio, bioId = 0) => {
  if (!isNonEmptyVariable(inmateBio)) return null;
  const { fName, mName, lName, ...rest } = inmateBio;
  return {
    ...rest,
    bioId,
    fName: fName,
    mName: mName,
    lName: lName,
  };
};

const BailPaymentDetailForm = ({
  bookingBails,
  singleBookingPayments,
  paymentForm,
  setSingleBookingPayments,
  chargesData,
  startingPoint = "",
  // Reports the extra table's field keys/type map/current PK up to the
  // parent (ChargesBailPaymentsDetails.jsx) whenever they change, since the
  // parent — not this form — builds and posts the actual save payload. See
  // BookingBailPaymentsExtra notes below.
  onExtraMetaChange,
}) => {
  const { showDialog } = useDialog();
  const { bookings, bookingsFieldsEnabled } = useSelector((state) => state?.bookings);

  // -----------------------------------------------------------------------
  // BookingBailPaymentsExtra — dynamic per-client fields. PCI shows these
  // under "Additional Payment Information", right before "Payment
  // Administrative Information" in BookingBailPaymentsInterface.asp — same
  // spot below.
  // -----------------------------------------------------------------------
  const [bailPaymentExtraFields, setBailPaymentExtraFields] = useState([]);
  const [bailPaymentExtraFieldsReady, setBailPaymentExtraFieldsReady] =
    useState(false);
  const bailPaymentExtraTypeMapRef = useRef({});
  const [bailPaymentExtraFormValues, setBailPaymentExtraFormValues] =
    useState(null);
  const bailPaymentExtraIDRef = useRef(0);

  useEffect(() => {
    api
      .get(`${API_ENDPOINTS.getTableColumns}/BookingBailPaymentsExtra`)
      .then((response) => {
        const tableData = response?.data?.filter((d) =>
          d?.columnName?.endsWith("_U"),
        );
        const converted = convertApiFields(
          tableData,
          "BookingBailPaymentsExtra",
        );

        const typeMap = {};
        converted.forEach((c) => {
          typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
        });
        bailPaymentExtraTypeMapRef.current = typeMap;

        setBailPaymentExtraFields(converted);
        setBailPaymentExtraFieldsReady(true);
      })
      .catch((error) => {
        console.error(
          "Request failed:",
          error.response ? error.response.data : error.message,
        );
      });
  }, []);

  // Fetches the saved BookingBailPaymentsExtra row when editing an existing
  // payment — GET /api/extra/BookingBailPaymentsExtra/bookingBailPaymentID/
  // {id}, same generic endpoint the other Extra tables use.
  useEffect(() => {
    if (!bailPaymentExtraFieldsReady) return;

    const bookingBailPaymentID = singleBookingPayments?.bookingBailPaymentID;
    if (!bookingBailPaymentID) {
      bailPaymentExtraIDRef.current = 0;
      setBailPaymentExtraFormValues(null);
      return;
    }

    fetchExtraTableRecord(
      "BookingBailPaymentsExtra",
      "bookingBailPaymentID",
      bookingBailPaymentID,
    )
      .then((result) => {
        if (!result) {
          bailPaymentExtraIDRef.current = 0;
          setBailPaymentExtraFormValues(null);
          return;
        }
        bailPaymentExtraIDRef.current =
          result.raw?.BookingBailPaymentsExtraID ?? 0;

        const withDates = Object.keys(result.values).reduce((acc, key) => {
          const val = result.values[key];
          const htmlType = bailPaymentExtraTypeMapRef.current[key];
          const isDateField = htmlType === "date" || htmlType === "datetime";
          acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
          return acc;
        }, {});
        setBailPaymentExtraFormValues(withDates);
      })
      .catch((error) => {
        console.error(
          "Request failed:",
          error.response ? error.response.data : error.message,
        );
      });
  }, [singleBookingPayments?.bookingBailPaymentID, bailPaymentExtraFieldsReady]);

  // Report field keys/type map/current PK up to the parent whenever they
  // change — the parent needs these at save time, not render time.
  useEffect(() => {
    onExtraMetaChange?.({
      fieldKeys: bailPaymentExtraFields.map((f) => f.columnName),
      typeMap: bailPaymentExtraTypeMapRef.current,
      extraID: bailPaymentExtraIDRef.current,
    });
  }, [bailPaymentExtraFields, bailPaymentExtraFormValues]);

  // Pushed separately from the main paymentFormState sync effect so this
  // fetch resolving doesn't re-trigger that effect's other side effects.
  useEffect(() => {
    if (isNonEmptyVariable(bailPaymentExtraFormValues)) {
      paymentForm.setFieldsValue(bailPaymentExtraFormValues);
    }
  }, [bailPaymentExtraFormValues]);

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md).
  const {
    headers: bailPaymentBailsHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("Bookings:BookingBailPaymentBails", BAIL_PAYMENT_BAILS_HEADERS);
  const [paymentFormState, setPaymentFormState] = useState(null);
  const [paymentType, setPaymentType] = useState(null);
  const [paymentTypeConfirmModal, setPaymentTypeConfirmModal] = useState(null);
  const [modalForm] = Form.useForm();
  const [isPaymentCoversModal, setIsPaymentCoversModal] = useState(false);
  const [selectBailDetail, setSelectBailDetail] = useState(null);
  const [bailrecords, setBailRecords] = useState([]);
  const fullSearch = useSelector((state) => state.fullSearch);
  const paymentTypeID = Form.useWatch("paymentTypeID", paymentForm);

  useEffect(() => {
    console.log("singleBookingPayments: ", singleBookingPayments);

    if (isNonEmptyVariable(singleBookingPayments)) {
      const paidDateTime = dayjs(
        `${
          isNonEmptyVariable(singleBookingPayments?.datePaid)
            ? singleBookingPayments?.datePaid
            : ""
        } ${
          isNonEmptyVariable(singleBookingPayments?.timePaid)
            ? singleBookingPayments?.timePaid
            : ""
        }`,
      );
      let br = [];
      if (singleBookingPayments?.bookingBailPaymentBails) {
        br = singleBookingPayments?.bookingBailPaymentBails?.map((b) => {
          return {
            ...b,
            bailType: getRefListNameByID("BondType", b?.bailTypeID) || null,
            charge: getRefListNameByID("Charges", b?.charge) || null,
          };
        });

        const paymentAmount =
          singleBookingPayments?.bookingBailPaymentBails?.reduce((acc, b) => {
            return (acc += parseFloat(b?.bailAmount));
          }, 0);

        paymentForm.setFieldValue("paymentAmount", paymentAmount);
      }

      // Personal Information section fields (fName/lName/doorNo/etc.) live
      // on the payer's bio record (singleBookingPayments.bio[0]), not on
      // the payment record itself - map that bio's field names onto this
      // form's field names so an existing payment's Personal Information
      // shows up on Details/Edit. Address falls back to bioAddresses'
      // nested address record when the bio's own top-level street/city/
      // etc. aren't set (e.g. after "bring over inmate's info", which only
      // populates bioAddresses).
      const payerBio = singleBookingPayments?.bio?.[0];
      const payerAddress = payerBio?.bioAddresses?.[0]?.addresses?.[0] || {};
      const personalInfo = isNonEmptyVariable(payerBio)
        ? {
            fName: payerBio?.fName,
            mName: payerBio?.mName,
            lName: payerBio?.lName,
            suffix: payerBio?.suffix,
            age: payerBio?.ageAtEntry,
            sex: payerBio?.sex,
            doorNo: payerBio?.streetNumber || payerAddress?.addressStreetNumber,
            street1: payerBio?.street1 || payerAddress?.addressStreet1,
            street2: payerBio?.street2 || payerAddress?.addressStreet2,
            aptNumber:
              payerBio?.apartmentNumber || payerAddress?.addressApartmentNumber,
            city: payerBio?.city || payerAddress?.addressCity,
            stateID: payerBio?.state || payerAddress?.addressState,
            zip: payerBio?.zip || payerAddress?.addressZip,
          }
        : {};

      setBailRecords(() => br);
      setPaymentFormState(() => ({
        ...singleBookingPayments,
        ...personalInfo,
        paymentPaidDateTime: paidDateTime?.isValid() ? paidDateTime : "",
      }));
    } else {
      setPaymentFormState(null);
    }
  }, [singleBookingPayments]);

  useEffect(() => {
    if (isNonEmptyVariable(paymentFormState)) {
      paymentForm.setFieldsValue(paymentFormState);
    } else {
      paymentForm.resetFields();
      if (!fullSearch) {
        paymentForm?.setFieldValue("paymentPaidDateTime", dayjs());
      }
    }
  }, [paymentFormState]);

  const handlePaymentModalVisible = () => {
    setIsPaymentCoversModal(!isPaymentCoversModal);
  };

  const cancelAction = () => {
    setSelectBailDetail(null);
    setIsPaymentCoversModal(() => false);
  };

  const onRowClick = (v) => {
    setSelectBailDetail(v);
  };

  const deleteAction = () => {
    console.log(singleBookingPayments, selectBailDetail);

    if (singleBookingPayments?.bookingBailPaymentBails?.length) {
      const bookingBailPaymentBails =
        singleBookingPayments?.bookingBailPaymentBails?.filter(
          (b) =>
            (isNonEmptyVariable(b?.bookingBailID) &&
              b?.bookingBailID !== selectBailDetail?.bookingBailID) ||
            b?.bookingBailUuid !== selectBailDetail?.bookingBailUuid,
        );
      setSingleBookingPayments(() => ({
        ...singleBookingPayments,
        bookingBailPaymentBails: bookingBailPaymentBails,
      }));
    }
  };

  const onModalSaveClick = () => {
    modalForm?.validateFields().then((m) => {
      let bookingBailPaymentBails = [];
      console.log(m);

      const bookingBail = bookingBails?.find(
        (bb) => bb?.value === m?.bookingBailUuid,
      )?.data;

      console.log(singleBookingPayments);
      console.log(
        singleBookingPayments?.bookingBailPaymentBails?.findIndex(
          (bp) =>
            bp?.bookingBailID === m?.bookingBailUuid ||
            bp?.bookingBailUuid === m?.bookingBailUuid,
        ),
      );

      if (
        isNonEmptyVariable(singleBookingPayments?.bookingBailPaymentBails) &&
        singleBookingPayments?.bookingBailPaymentBails?.findIndex(
          (bp) =>
            bp?.bookingBailID === m?.bookingBailUuid ||
            bp?.bookingBailUuid === m?.bookingBailUuid,
        ) !== -1
      ) {
        showDialog("info", {
          title: "Duplicate Bail",
          content: "This bail already selected",
          confirmButtonText: "Ok",
        });
        modalForm.setFieldValue("bookingBailUuid", "");
        return;
      }

      if (singleBookingPayments?.bookingBailPaymentBails?.length) {
        bookingBailPaymentBails =
          singleBookingPayments?.bookingBailPaymentBails;
      }

      bookingBailPaymentBails.push(bookingBail);
      bookingBailPaymentBails = bookingBailPaymentBails?.map((bbp) => {
        let bailCharge = null;
        chargesData?.forEach((c) => {
          if (
            (isNonEmptyVariable(c?.bookingBails?.[0]?.bookingBailID) &&
              c?.bookingBails?.[0]?.bookingBailID === bbp?.bookingBailID) ||
            c?.bookingBails?.[0]?.bookingBailUuid === bbp?.bookingBailUuid
          ) {
            bailCharge = c;
          }
        });

        console.log("bailCharge", bailCharge);

        return {
          ...bbp,
          charge: bailCharge?.chargeDescriptionID || "",
          isPaymentBailAdd: true,
        };
      });

      setSingleBookingPayments(() => ({
        ...singleBookingPayments,
        bookingBailPaymentBails: bookingBailPaymentBails,
      }));
      modalForm.setFieldValue("bookingBailUuid", "");
    });
    cancelAction();
  };

  const handlePaymentTypeChange = (type) => {
    setPaymentType(type);
    if (type !== 54820003) {
      setPaymentTypeConfirmModal(true);
    }
  };

  // Brings over the inmate's Name + Address + Age/Sex onto the payer fields below.
  const bringOverInmateNameAndAddress = () => {
    const bio = bookings?.bio?.[0];
    if (!isNonEmptyVariable(bio)) return;

    const address = bio?.bioAddresses?.[0]?.addresses?.[0] || {};

    paymentForm.setFieldsValue({
      fName: bio?.fName,
      lName: bio?.lName,
      ...(isNonEmptyVariable(bio?.mName) ? { mName: bio?.mName } : {}),
      ...(isNonEmptyVariable(bio?.suffix) ? { suffix: bio?.suffix } : {}),
      ...(isNonEmptyVariable(bio?.sex) ? { sex: bio?.sex } : {}),
      ...(isNonEmptyVariable(bio?.dob)
        ? { age: findAgefromDOB(bio?.dob) }
        : {}),
      ...(isNonEmptyVariable(address?.addressStreetNumber)
        ? { doorNo: address?.addressStreetNumber }
        : {}),
      ...(isNonEmptyVariable(address?.addressStreet1)
        ? { street1: address?.addressStreet1 }
        : {}),
      ...(isNonEmptyVariable(address?.addressStreet2)
        ? { street2: address?.addressStreet2 }
        : {}),
      ...(isNonEmptyVariable(address?.addressApartmentNumber)
        ? { aptNumber: address?.addressApartmentNumber }
        : {}),
      ...(isNonEmptyVariable(address?.addressCity)
        ? { city: address?.addressCity }
        : {}),
      ...(isNonEmptyVariable(address?.addressState)
        ? { stateID: address?.addressState }
        : {}),
      ...(isNonEmptyVariable(address?.addressZip)
        ? { zip: address?.addressZip }
        : {}),
    });

    // The form only has inputs for the handful of fields set above, but the
    // save payload's "bio" node is a full Bio record (ssn, race, height,
    // weight, dob, hair/eyes, dl info, emergency contact, etc.). "Bring
    // over" means bring over all of it, not just what's visible on screen -
    // so stash the full inmate bio here too, translated to this payload's
    // field names. Only the bioId is stripped: the payer still gets their
    // own bio row, not the inmate's, so reuse whatever this payment already
    // saved (edit) or fall back to 0 (new payment) rather than the inmate's.
    setSingleBookingPayments((prev) => ({
      ...(prev || {}),
      bio: [mapInmateBioToPayerBio(bio, prev?.bio?.[0]?.bioId || 0)],
    }));
  };

  return (
    <>
      <FieldSecurityProvider
        entity={SCHEMA_NAME.BOOKING_BAIL_PAYMENTS}
        startingPoint={startingPoint}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
      >
        <StyledSectionHeading>
          <span>Payment Information</span>
        </StyledSectionHeading>
        <Form form={paymentForm} disabled={!bookingsFieldsEnabled}>
          <StyledGridRow gutter={16}>
            <StyledGridCol lg={5}>
              <SecureFormItem
                fieldName="paymentTypeID"
                formItemProps={{
                  name: "paymentTypeID",
                  label: "Payment Type",
                  rules: !fullSearch
                    ? [{ required: true, message: "Payment type is required" }]
                    : [],
                }}
              >
                <Select
                  onChange={handlePaymentTypeChange}
                  options={getOptionsFromRefList(
                    "Payment_Type",
                    false,
                    false,
                    true,
                  )}
                  disabled={!bookingsFieldsEnabled}
                />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={4}>
              <SecureFormItem
                fieldName="paymentAmount"
                formItemProps={{ name: "paymentAmount", label: "Amount" }}
                overrideDisabled={!fullSearch}
              >
                <TextField type="number" />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={5}>
              <SecureFormItem
                fieldName="paymentPaidDateTime"
                formItemProps={{
                  name: "paymentPaidDateTime",
                  label: "Date/Time Paid",
                }}
              >
                <CommonDatePicker
                  format="MM/DD/YYYY HH:mm"
                  placeholder="MM/DD/YYYY HH:mm"
                  showTime={true}
                  disabled={!bookingsFieldsEnabled}
                />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={5}>
              <SecureFormItem
                fieldName="receiptNumber"
                formItemProps={{
                  name: "receiptNumber",
                  label: "Receipt #",
                  rules: !fullSearch
                    ? paymentTypeID === 54820001 ||
                      paymentTypeID === 54820002 ||
                      paymentTypeID === 54820003
                      ? [
                          {
                            required: true,
                            message: "Receipt number is required",
                          },
                        ]
                      : []
                    : [],
                }}
              >
                <TextField type="number" />
              </SecureFormItem>
            </StyledGridCol>
            {paymentTypeID === 54820002 && (
              <StyledGridCol lg={5}>
                <SecureFormItem
                  fieldName="checkNumber"
                  formItemProps={{
                    name: "checkNumber",
                    label: "Check Number #",
                    rules: !fullSearch
                      ? [
                          {
                            required: true,
                            message: "Check number is required",
                          },
                        ]
                      : [],
                  }}
                >
                  <TextField type="number" disabled={!bookingsFieldsEnabled} />
                </SecureFormItem>
              </StyledGridCol>
            )}
            <SecureFormItem
              fieldName="comments"
              formItemProps={{ name: "comments", label: "Comments" }}
            >
              <TextField
                inputType="textarea"
                disabled={!bookingsFieldsEnabled}
              />
            </SecureFormItem>
          </StyledGridRow>

          <StyledSectionHeading>
            <span>Bails This Payment Covers</span>
          </StyledSectionHeading>
          <StyledSubTableContainer>
            <StyledFlex justify="end" gap="10px" marginBottom="10px">
              <Button
                onClick={handlePaymentModalVisible}
                disabled={!bookingsFieldsEnabled || isConfiguring || !(hasCheckSecurity(
                Security_Types.functionality,
                Entity_types.subModule,
                LABELS_CAPITAL.ADD,
                MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                SUB_MODULES_LIST_WITH_ID.Arrest_Report
              ))}>
                <PlusOutlined /> Add
              </Button>
              <Button
                disabled={!bookingsFieldsEnabled || selectBailDetail === null || isConfiguring || !(hasCheckSecurity(
                Security_Types.functionality,
                Entity_types.subModule,
                LABELS_CAPITAL.DELETE,
                MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                SUB_MODULES_LIST_WITH_ID.Arrest_Report
              ))} onClick={deleteAction}>
                Delete
              </Button>
              <Button onClick={cancelAction} disabled={selectBailDetail === null || isConfiguring}>
                Cancel
              </Button>

              {/* Config — drag-reorder/resize columns */}
              {!isConfiguring ? (
                <Button htmlType="button" onClick={startConfiguring}>
                  {configButtonLabel}
                </Button>
              ) : (
                <Popconfirm
                  title="Do you want to save the current list view configuration?"
                  onConfirm={confirmSave}
                  onCancel={discardChanges}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ disabled: false }}
                  cancelButtonProps={{ disabled: false }}
                >
                  <Button htmlType="button">{configButtonLabel}</Button>
                </Popconfirm>
              )}
            </StyledFlex>

            <Table
              ref={dataTableRef}
              headers={bailPaymentBailsHeaders}
              draggable={isConfiguring}
              data={bailrecords || []}
              onRowClick={onRowClick}
              selected={selectBailDetail === null}
            />
          </StyledSubTableContainer>

          {paymentType === 54820003 ? (
            <>
              <StyledSectionHeading marginTop="25px">
                <span>Bonding Information</span>
              </StyledSectionHeading>

              <StyledGridRow gutter={16}>
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName="bondedBy"
                    formItemProps={{ name: "bondedBy", label: "Bondsman Name" }}
                  >
                    <TextField />
                  </SecureFormItem>
                </StyledGridCol>
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName="bondPhone"
                    formItemProps={{ name: "bondPhone", label: "Phone" }}
                  >
                    <TextField />
                  </SecureFormItem>
                </StyledGridCol>

                <StyledGridCol xs={6}>
                  <FormItem name="bondCity" label="City">
                    <TextField />
                  </FormItem>
                </StyledGridCol>
                <StyledGridCol xs={6}>
                  <FormItem name="bondStateID" label="ST">
                    <Select
                      options={getOptionsFromRefList(
                        "USState",
                        false,
                        false,
                        true,
                      )}
                      placeholder="ST"
                      showSearch={false}
                      variant="outlined"
                    />
                  </FormItem>
                </StyledGridCol>
                <StyledGridCol xs={6}>
                  <FormItem name="bondZip" label="Zip Code">
                    <TextField />
                  </FormItem>
                </StyledGridCol>
                <StyledGridCol xs={18}>
                  <FormItem name="bondStreet1" label="Bond Address">
                    <TextField inputType="textarea" id="addressLineOne" />
                  </FormItem>
                </StyledGridCol>
              </StyledGridRow>
            </>
          ) : (
            <>
              <StyledSectionHeading marginTop="25px">
                <span>Personal Information</span>
              </StyledSectionHeading>
              {/* <Personal person={PERSON.BAILPAYMENT} /> */}
              <StyledGridRow gutter={16}>
                {/* First Name */}
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName="fName"
                    formItemProps={{ name: "fName", label: "First Name" }}
                  >
                    <TextField />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Middle Name */}
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName="mName"
                    formItemProps={{ name: "mName", label: "Middle Name" }}
                  >
                    <TextField />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Last Name */}
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName="lName"
                    formItemProps={{ name: "lName", label: "Last Name" }}
                  >
                    <TextField />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Suffix */}
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName="suffix"
                    formItemProps={{ name: "suffix", label: "Suffix" }}
                  >
                    <Select
                      options={[
                        { label: "Jr", value: "Jr" },
                        { label: "Sr", value: "Sr" },
                        { label: "II", value: "II" },
                        { label: "III", value: "III" },
                        { label: "IV", value: "IV" },
                      ]}
                      placeholder="Select Suffix"
                      variant="outlined"
                      disabled={!bookingsFieldsEnabled}
                    />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Age */}
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName="age"
                    formItemProps={{ name: "age", label: "Age" }}
                  >
                    <TextField />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Sex */}
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName="sex"
                    formItemProps={{ name: "sex", label: "Sex" }}
                  >
                    <Select
                      options={[
                        { label: "Male", value: "Male" },
                        { label: "Female", value: "Female" },
                        { label: "Other", value: "Other" },
                      ]}
                      placeholder="Select Sex"
                      variant="outlined"
                    />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Door No */}
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName="doorNo"
                    formItemProps={{ name: "doorNo", label: "Door No" }}
                  >
                    <TextField />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Street 1 */}
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName="street1"
                    formItemProps={{ name: "street1", label: "Street" }}
                  >
                    <TextField />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Street 2 */}
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName="street2"
                    formItemProps={{ name: "street2", label: "Street 2" }}
                  >
                    <TextField />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Apt Number */}
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName="aptNumber"
                    formItemProps={{ name: "aptNumber", label: "Apt Number" }}
                  >
                    <TextField />
                  </SecureFormItem>
                </StyledGridCol>

                {/* City */}
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName="city"
                    formItemProps={{ name: "city", label: "City" }}
                  >
                    <TextField />
                  </SecureFormItem>
                </StyledGridCol>

                {/* State */}
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName="stateID"
                    formItemProps={{ name: "stateID", label: "State" }}
                  >
                    <Select
                      options={getOptionsFromRefList(
                        "USState",
                        false,
                        false,
                        true,
                      )}
                      placeholder="State"
                      variant="outlined"
                    />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Zip */}
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName="zip"
                    formItemProps={{ name: "zip", label: "Zip Code" }}
                  >
                    <TextField />
                  </SecureFormItem>
                </StyledGridCol>
              </StyledGridRow>
            </>
          )}

          {/* BookingBailPaymentsExtra dynamic fields — PCI shows these
              right before "Payment Administrative Information" (see the
              matching heading just below). Still inside this component's
              main <Form form={paymentForm}>. */}
          {bailPaymentExtraFields.length > 0 && (
            <>
              <StyledSectionHeading>
                <span>Additional Payment Information</span>
              </StyledSectionHeading>
              <StyledGridRow gutter={16}>
                {bailPaymentExtraFields.map((sf, index) => {
                  const view = hasCheckSecurity(
                    "Functionality", "field", "VIEW", "", "",
                    "BookingBailPaymentsExtra", sf.columnName, startingPoint,
                  );
                  const edit = hasCheckSecurity(
                    "Functionality", "field", "MODIFY", "", "",
                    "BookingBailPaymentsExtra", sf.columnName, startingPoint,
                  );
                  return (
                    <DynamicForm
                      key={index}
                      formAttributes={sf}
                      form={paymentForm}
                      bookingsFieldsEnabled={bookingsFieldsEnabled}
                      fullSearch={fullSearch}
                      view={view}
                      edit={edit}
                    />
                  );
                })}
              </StyledGridRow>
            </>
          )}

          <StyledSectionHeading>
            <span>Payment Administrative Information</span>
          </StyledSectionHeading>
          <StyledGridRow gutter={16}>
            <StyledGridCol lg={4}>
              <FormItem label="ID">
                <TextField />
              </FormItem>
            </StyledGridCol>

            <StyledGridCol lg={6}>
              <FormItem label="Date Entered">
                <TextField />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="Time Entered">
                <TextField />
              </FormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <FormItem label="User">
                <TextField />
              </FormItem>
            </StyledGridCol>
          </StyledGridRow>
        </Form>
      </FieldSecurityProvider>

      {isPaymentCoversModal && (
        <CustomModal
          size="md"
          header="Bail Payment Details"
          show={isPaymentCoversModal}
          onHide={handlePaymentModalVisible}
          onSaveClick={onModalSaveClick}
        >
          <Form
            form={modalForm}
            initialValue={{
              bookingBailUuid: "",
            }}
          >
            <FormItem
              name="bookingBailUuid"
              label="Select a bail that this payment is going to cover"
              rules={
                !fullSearch
                  ? [
                      {
                        required: true,
                        message: "Field is required",
                      },
                    ]
                  : []
              }
            >
              <Select options={bookingBails} />
            </FormItem>
          </Form>
        </CustomModal>
      )}

      {paymentTypeConfirmModal && (
        <CustomModal
          size="md"
          header=""
          submitButtonText="Okay"
          show={paymentTypeConfirmModal}
          onHide={() => setPaymentTypeConfirmModal(false)}
          onSaveClick={() => {
            bringOverInmateNameAndAddress();
            setPaymentTypeConfirmModal(false);
          }}
        >
          <h4 style={{ color: "var(--text-black)", textAlign: "center" }}>
            Would you like to bring over the inmate's name and address?
          </h4>
        </CustomModal>
      )}
    </>
  );
};
export default BailPaymentDetailForm;
